import { Skeleton } from 'antd'
import classNames from 'classnames'
import type { EncounterPhotoApiItem } from 'features/Procedure/state/procedure-api.type'
import { useFetchEncounterPhotosQuery } from 'features/Procedure/state/procedure.api'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { PhotoPreviewModal } from '../../photos/PhotoPreviewModal'
import { parseCategoryFromFileName, parsePositionFromFileName } from '../../photos/photos.utils'
import styles from './photos-comparison.module.scss'

type PhotoCategory = 'before' | 'after'

interface PhotoData {
  id: string
  imageUrl: string
  label: string
  note?: string
  position: number
}

const isPhotoCategory = (value: unknown): value is PhotoCategory =>
  value === 'before' || value === 'after'

const mapApiPhotosToPositions = (
  items: EncounterPhotoApiItem[],
): Record<PhotoCategory, PhotoData[]> => {
  const result: Record<PhotoCategory, PhotoData[]> = {
    before: [],
    after: [],
  }

  items.forEach((item) => {
    let category: PhotoCategory = 'before'
    if (isPhotoCategory(item.category)) {
      category = item.category
    } else if (item.filename) {
      category = parseCategoryFromFileName(item.filename)
    }

    const position = parsePositionFromFileName(item.filename)

    result[category].push({
      id: item.id.toString(),
      imageUrl: item.thumbnail || item.url || item.data || '',
      label: item.filename || `${category} ${result[category].length + 1}`,
      note: item.note ?? undefined,
      position,
    })
  })

  result.before.sort((a, b) => a.position - b.position)
  result.after.sort((a, b) => a.position - b.position)

  return result
}

export type PhotosComparisonProps = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  selectedComparisonEncounterUid: string | null
  className?: string
}

export const PhotosComparison = ({
  selectedPatientId,
  selectedEncounterUid,
  selectedComparisonEncounterUid,
  className,
}: PhotosComparisonProps) => {
  const { siteId } = useParams<{ siteId: string }>()
  const [previewPhotoId, setPreviewPhotoId] = useState<number | null>(null)
  const [previewEncounterUid, setPreviewEncounterUid] = useState<string | null>(null)

  const shouldSkipMainQuery = !siteId || !selectedPatientId || !selectedEncounterUid
  const shouldSkipComparisonQuery = !siteId || !selectedPatientId || !selectedComparisonEncounterUid

  const {
    data: mainPhotos,
    isFetching: isFetchingMain,
    refetch: refetchMain,
  } = useFetchEncounterPhotosQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      encounterUid: selectedEncounterUid!,
    },
    { skip: shouldSkipMainQuery },
  )

  const {
    data: comparisonPhotos,
    isFetching: isFetchingComparison,
    refetch: refetchComparison,
  } = useFetchEncounterPhotosQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      encounterUid: selectedComparisonEncounterUid!,
    },
    { skip: shouldSkipComparisonQuery },
  )

  // RTK Query already fetches on arg change / cache miss — the previous manual
  // `useEffect(() => refetchMain(), [shouldSkipMainQuery, refetchMain])` only
  // duplicated work on every mount and made Photos visibly slow on first open.

  const mainPhotosByPosition = useMemo(
    () => (mainPhotos ? mapApiPhotosToPositions(mainPhotos) : { before: [], after: [] }),
    [mainPhotos],
  )

  const comparisonPhotosByPosition = useMemo(
    () =>
      comparisonPhotos ? mapApiPhotosToPositions(comparisonPhotos) : { before: [], after: [] },
    [comparisonPhotos],
  )

  const allPositions = useMemo(() => {
    const positions = new Set<number>()

    mainPhotosByPosition.before.forEach((p) => positions.add(p.position))
    mainPhotosByPosition.after.forEach((p) => positions.add(p.position))
    comparisonPhotosByPosition.before.forEach((p) => positions.add(p.position))
    comparisonPhotosByPosition.after.forEach((p) => positions.add(p.position))

    return Array.from(positions).sort((a, b) => a - b)
  }, [mainPhotosByPosition, comparisonPhotosByPosition])

  const getPhotoAtPosition = (photos: PhotoData[], position: number): PhotoData | null => {
    return photos.find((p) => p.position === position) || null
  }

  const handlePhotoClick = (photoId: string, encounterUid: string | null) => {
    const photoIdNum = parseInt(photoId, 10)
    if (!isNaN(photoIdNum) && encounterUid) {
      setPreviewPhotoId(photoIdNum)
      setPreviewEncounterUid(encounterUid)
    }
  }

  const renderPhotoCell = (
    photo: PhotoData | null,
    isLoading: boolean,
    encounterUid: string | null,
    label: string,
  ) => {
    if (isLoading) {
      return (
        <>
          <div className={styles.cellLabel}>{label}</div>
          <div className={styles.photoCard}>
            <Skeleton.Image active style={{ width: '100%', height: '100%' }} />
          </div>
        </>
      )
    }

    if (!photo) {
      return (
        <>
          <div className={styles.cellLabel}>{label}</div>
          <div className={styles.emptyState}>
            <span>No photo</span>
          </div>
        </>
      )
    }

    return (
      <>
        <div className={styles.cellLabel}>{label}</div>
        <div className={styles.photoCard} onClick={() => handlePhotoClick(photo.id, encounterUid)}>
          <img
            src={photo.imageUrl}
            alt={photo.label}
            className={styles.photoImage}
            loading='lazy'
            decoding='async'
          />
        </div>
      </>
    )
  }

  const renderPositionRow = (position: number) => {
    const mainBefore = getPhotoAtPosition(mainPhotosByPosition.before, position)
    const mainAfter = getPhotoAtPosition(mainPhotosByPosition.after, position)
    const comparisonBefore = getPhotoAtPosition(comparisonPhotosByPosition.before, position)
    const comparisonAfter = getPhotoAtPosition(comparisonPhotosByPosition.after, position)

    return (
      <div key={position} className={styles.positionBlock}>
        <div className={styles.positionHeader}>Position {position}</div>
        <div className={styles.photosGrid}>
          {/* Main Row */}
          <div className={styles.photoCell}>
            {renderPhotoCell(mainBefore, isFetchingMain, selectedEncounterUid, 'Main Before')}
          </div>
          <div className={styles.photoCell}>
            {renderPhotoCell(mainAfter, isFetchingMain, selectedEncounterUid, 'Main After')}
          </div>

          {/* Comparison Row */}
          <div className={styles.photoCell}>
            {renderPhotoCell(
              comparisonBefore,
              isFetchingComparison,
              selectedComparisonEncounterUid,
              'Comparison Before',
            )}
          </div>
          <div className={styles.photoCell}>
            {renderPhotoCell(
              comparisonAfter,
              isFetchingComparison,
              selectedComparisonEncounterUid,
              'Comparison After',
            )}
          </div>
        </div>
      </div>
    )
  }

  const isLoading = isFetchingMain || isFetchingComparison
  const hasNoPhotos = allPositions.length === 0 && !isLoading

  return (
    <div className={classNames(styles.container, className)}>
      {hasNoPhotos ? (
        <div className={styles.noPhotosMessage}>No photos available for comparison</div>
      ) : isLoading && allPositions.length === 0 ? (
        <div className={styles.positionBlock}>
          <div className={styles.positionHeader}>Position 1</div>
          <div className={styles.photosGrid}>
            <div className={styles.photoCell}>
              {renderPhotoCell(null, true, null, 'Main Before')}
            </div>
            <div className={styles.photoCell}>
              {renderPhotoCell(null, true, null, 'Main After')}
            </div>
            <div className={styles.photoCell}>
              {renderPhotoCell(null, true, null, 'Comparison Before')}
            </div>
            <div className={styles.photoCell}>
              {renderPhotoCell(null, true, null, 'Comparison After')}
            </div>
          </div>
        </div>
      ) : (
        allPositions.map(renderPositionRow)
      )}

      <PhotoPreviewModal
        open={previewPhotoId !== null}
        photoId={previewPhotoId}
        category={null}
        onClose={() => {
          setPreviewPhotoId(null)
          setPreviewEncounterUid(null)
        }}
        onDelete={() => {
          if (previewEncounterUid === selectedEncounterUid && !shouldSkipMainQuery) {
            refetchMain()
          } else if (
            previewEncounterUid === selectedComparisonEncounterUid &&
            !shouldSkipComparisonQuery
          ) {
            refetchComparison()
          }
        }}
        onRetake={() => {}}
        selectedPatientId={selectedPatientId}
        selectedEncounterUid={previewEncounterUid}
        readOnly
      />
    </div>
  )
}
